import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, Image, MessageCircle, Send, X } from 'react-feather';
import { supportAPI } from '../api';
import { useLanguage } from '../context/LanguageContext';
import AdsDisplay from '../components/AdsDisplay';
import './Support.css';

const MAX_ATTACHMENTS = 3;
const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif']);

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function filesToAttachments(files) {
  const selected = Array.from(files || []);
  if (selected.length > MAX_ATTACHMENTS) {
    throw new Error(`Upload up to ${MAX_ATTACHMENTS} images at a time.`);
  }

  return Promise.all(selected.map(async (file) => {
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      throw new Error('Only PNG, JPG, WEBP, or GIF screenshots are allowed.');
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      throw new Error('Each screenshot must be 2 MB or smaller.');
    }

    return {
      filename: file.name,
      mime_type: file.type,
      size: file.size,
      data_url: await readFileAsDataUrl(file),
    };
  }));
}

function AttachmentPicker({ attachments, onChange, disabled }) {
  return (
    <div className="support-attachments">
      <label className="support-attachment-picker">
        <Image size={16} />
        <span>Add screenshots</span>
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          multiple
          disabled={disabled}
          onChange={async (e) => {
            try {
              onChange(await filesToAttachments(e.target.files));
            } catch (err) {
              onChange(attachments, err.message);
            } finally {
              e.target.value = '';
            }
          }}
        />
      </label>
      {attachments.length > 0 && (
        <div className="support-attachment-previews">
          {attachments.map((attachment, index) => (
            <div className="support-attachment-preview" key={`${attachment.filename}-${index}`}>
              <img src={attachment.data_url} alt={attachment.filename} />
              <button type="button" onClick={() => onChange(attachments.filter((_, i) => i !== index))}>
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MessageThread({ messages }) {
  return (
    <div className="support-thread">
      {(messages || []).map(message => (
        <div
          className={`support-thread-message ${message.sender_role === 'user' ? 'from-user' : 'from-admin'}`}
          key={message.id}
        >
          <div className="support-thread-meta">
            <strong>{message.sender_role === 'user' ? 'You' : 'Support'}</strong>
            <time>{new Date(message.created_at).toLocaleString()}</time>
          </div>
          <p>{message.message}</p>
          {message.attachments?.length ? (
            <div className="support-message-images">
              {message.attachments.map(attachment => (
                <a href={attachment.data_url} target="_blank" rel="noreferrer" key={attachment.id}>
                  <img src={attachment.data_url} alt={attachment.filename} />
                </a>
              ))}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export default function Support() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ subject: '', message: '', attachments: [] });
  const [replyForms, setReplyForms] = useState({});
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [replyingId, setReplyingId] = useState(null);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [expandedTicket, setExpandedTicket] = useState(null);

  const loadTickets = async () => {
    const res = await supportAPI.mine();
    setTickets(res.data.tickets || []);
  };

  useEffect(() => {
    loadTickets()
      .catch(() => setError('Failed to load support history.'))
      .finally(() => setLoading(false));
  }, []);

  const setReplyForm = (ticketId, next) => {
    setReplyForms(prev => ({
      ...prev,
      [ticketId]: { message: '', attachments: [], ...(prev[ticketId] || {}), ...next },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    setError('');

    if (form.subject.trim().length < 3) {
      setError('Subject must be at least 3 characters.');
      return;
    }

    if (form.message.trim().length < 10) {
      setError('Please describe your complaint in at least 10 characters.');
      return;
    }

    setSaving(true);
    try {
      await supportAPI.create({
        subject: form.subject.trim(),
        message: form.message.trim(),
        attachments: form.attachments,
      });
      setForm({ subject: '', message: '', attachments: [] });
      setMsg('Your support request has been submitted.');
      await loadTickets();
    } catch (err) {
      let errorMsg = 'Failed to submit support request.';
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        errorMsg = err.response.data.errors.map(e => `${e.param || e.path || 'field'}: ${e.msg}`).join(', ');
      } else if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      }
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleReply = async (ticketId) => {
    const reply = replyForms[ticketId] || { message: '', attachments: [] };
    setMsg('');
    setError('');

    if (!reply.message.trim()) {
      setError('Reply message is required.');
      return;
    }

    setReplyingId(ticketId);
    try {
      await supportAPI.reply(ticketId, {
        message: reply.message.trim(),
        attachments: reply.attachments || [],
      });
      setReplyForms(prev => ({ ...prev, [ticketId]: { message: '', attachments: [] } }));
      setMsg('Reply sent.');
      await loadTickets();
    } catch (err) {
      let errorMsg = 'Failed to send reply.';
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        errorMsg = err.response.data.errors.map(e => `${e.param || e.path || 'field'}: ${e.msg}`).join(', ');
      } else if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      }
      setError(errorMsg);
    } finally {
      setReplyingId(null);
    }
  };

  return (
    <div>
      <h1 className="page-title">{t('support.title', 'Support')}</h1>
      <p className="page-subtitle">{t('support.subtitle', 'Send a complaint or request to the NiceBooster team')}</p>

      <div className="support-grid">
        <form className="card support-form-card" onSubmit={handleSubmit}>
          <div className="support-card-title">
            <MessageCircle size={20} />
            <h3>{t('support.new_request', 'New Request')}</h3>
          </div>

          {msg && (
            <div className="alert alert-success support-alert">
              <CheckCircle size={18} /> <span>{msg}</span>
            </div>
          )}

          {error && (
            <div className="alert alert-error support-alert">
              <AlertCircle size={18} /> <span>{error}</span>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Subject</label>
            <input
              className="form-input"
              value={form.subject}
              onChange={e => setForm({ ...form, subject: e.target.value })}
              placeholder="Example: Deposit not credited"
              maxLength={160}
              disabled={saving}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Complaint</label>
            <textarea
              className="form-input support-message-input"
              value={form.message}
              onChange={e => setForm({ ...form, message: e.target.value })}
              placeholder="Write what happened, include order ID or deposit reference if you have one."
              maxLength={5000}
              disabled={saving}
              required
            />
          </div>

          <AttachmentPicker
            attachments={form.attachments}
            disabled={saving}
            onChange={(attachments, attachmentError) => {
              if (attachmentError) setError(attachmentError);
              else setError('');
              setForm({ ...form, attachments });
            }}
          />

          <button className="btn btn-primary btn-lg support-submit-btn" disabled={saving}>
            {saving ? 'Sending...' : <><Send size={16} /> Send Request</>}
          </button>
        </form>

        <AdsDisplay adSlot="native-banner" className="native-banner" />

        <AdsDisplay adSlot="banner-320x50" className="banner" />

        <div className="card support-history-card">
          <h3>Recent Requests</h3>
          {loading ? (
            <div className="support-loading"><div className="spinner" /></div>
          ) : tickets.length ? (
            <div className="support-ticket-list">
              {tickets.map(ticket => {
                const reply = replyForms[ticket.id] || { message: '', attachments: [] };
                return (
                  <article className="support-ticket" key={ticket.id}>
                    <div className="support-ticket-top" style={{ cursor: 'pointer' }} onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)}>
                      <strong>#{ticket.id} {ticket.subject}</strong>
                      <span className={`badge badge-${ticket.status === 'resolved' ? 'active' : 'pending'}`}>
                        {ticket.status}
                      </span>
                    </div>
                    {expandedTicket === ticket.id && (
                      <>
                        <MessageThread messages={ticket.messages} />
                        <div className="support-reply-box">
                          <textarea
                            className="form-input support-reply-input"
                            value={reply.message}
                            onChange={e => setReplyForm(ticket.id, { message: e.target.value })}
                            placeholder="Reply to support..."
                            maxLength={5000}
                          />
                          <AttachmentPicker
                            attachments={reply.attachments || []}
                            disabled={replyingId === ticket.id}
                            onChange={(attachments, attachmentError) => {
                              if (attachmentError) setError(attachmentError);
                              else setError('');
                              setReplyForm(ticket.id, { attachments });
                            }}
                          />
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleReply(ticket.id)}
                            disabled={replyingId === ticket.id || !reply.message.trim()}
                          >
                            {replyingId === ticket.id ? 'Sending...' : 'Reply'}
                          </button>
                        </div>
                      </>
                    )}
                  </article>
                );
              })}
            </div>
          ) : (
            <p className="support-empty">You have not submitted any support requests yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
